import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { type PropsWithChildren, type ReactNode, type UIEvent, useMemo } from 'react';

import { type OptionType } from '@/api/common';
import { Checkbox, CheckboxGroup } from '@/components/Form';
import { Field, FieldLabel } from '@/components/Form/fields';
import { type FilterOperatorMulti, type FilterOperatorValue } from '@/components/InstantSearch';
import { Link, Text } from '@/components/typography';
import { cn } from '@/lib/utils';

type Props<T extends OptionType = OptionType> = {
  className?: string;
  options: T[] | undefined;
  selectionMode: 'none' | 'single' | 'multiple';
  value?: FilterOperatorMulti;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  error?: Error | null;
  renderOption?: (option: T) => ReactNode;
  onChange?: (value: FilterOperatorValue) => void;
  onScroll?: (event: UIEvent<HTMLElement>) => void;
  onValidChange?: (valid: boolean) => void;
};

const SYSTEM_STATUS_ID = 'C99-STATUS';
const EMPTY_OPTION_VALUE = '__C99_EMPTY_OPTION_VALUE__';

const encodeOptionId = (id: string) => (id === '' ? EMPTY_OPTION_VALUE : id);
const decodeOptionId = (id: string) => (id === EMPTY_OPTION_VALUE ? '' : id);

const CategoryFilter = <T extends OptionType = OptionType>({
  className,
  children,
  options,
  selectionMode,
  isLoading = false,
  isLoadingMore = false,
  error,
  onScroll,
  value,
  renderOption,
  onChange,
}: PropsWithChildren<Props<T>>) => {
  const handleSelectAll = () => {
    onChange?.({ operator: 'notIn', operand: [] });
  };

  const handleClear = () => {
    onChange?.({ operator: 'in', operand: [] });
  };

  const handleSelectedKeysChange = (keys?: string[]) => {
    const decodedKeys = keys?.map(decodeOptionId);
    const op = value?.operator ?? 'notIn';

    let newOperand: string[] = [];
    let newOperator = op;
    if (op === 'notIn') {
      newOperator = 'notIn';
      const notCheckedValues =
        options?.filter((option) => !decodedKeys?.includes(option.id)).map((option) => option.id) ??
        [];
      newOperand = notCheckedValues;
    } else {
      newOperator = 'in';
      newOperand = decodedKeys ?? [];
    }
    onChange?.({ operator: newOperator, operand: [...new Set(newOperand)] });
  };

  const listOptions = useMemo(() => {
    if (error) {
      return [
        {
          id: SYSTEM_STATUS_ID,
          name: t`Unable to load items, please refresh and try again`,
        } as T,
      ];
    }

    if (isLoading) {
      return [{ id: SYSTEM_STATUS_ID, name: t`Loading...` } as T];
    }

    if (isLoadingMore) {
      return options?.concat({ id: SYSTEM_STATUS_ID, name: t`Loading...` } as T);
    }

    return options;
  }, [options, isLoading, isLoadingMore, error]);

  const checkedValues = useMemo(() => {
    if (!value?.operator || value?.operator === 'notIn') {
      const operand = value?.operand ?? [];
      return (
        options
          ?.filter((option) => !operand.includes(option.id))
          .map((option) => encodeOptionId(option.id)) ?? []
      );
    } else {
      return (value?.operand ?? []).map(encodeOptionId);
    }
  }, [value, options]);

  const handleGroupChange = (keys: string[]) => {
    const decodedKeys = keys.map(decodeOptionId);
    if (selectionMode !== 'single') {
      handleSelectedKeysChange(decodedKeys);
      return;
    }

    const addedKey = decodedKeys.find((key) => !checkedValues.includes(encodeOptionId(key)));
    handleSelectedKeysChange(addedKey ? [addedKey] : []);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <Link variant="caption1" onClick={handleSelectAll}>
          <Trans>Select All</Trans>
        </Link>
        <Link variant="caption1" onClick={handleClear}>
          <Trans>Clear</Trans>
        </Link>
      </div>
      {children}
      <div
        className={cn(
          'flex max-h-62.5 w-full flex-col overflow-auto rounded-xs border p-2',
          'border-gray-200',
          className,
        )}
        onScroll={onScroll}
      >
        {selectionMode === 'none' ? (
          listOptions?.map((item) => {
            const isSystemStatus = item.id === SYSTEM_STATUS_ID;
            return (
              <div key={item.id} className="px-2 py-1.5">
                {isSystemStatus ? (
                  <Text variant="body2" color="grey" italic>
                    {item.name}
                  </Text>
                ) : (
                  <Text variant="body2">{renderOption ? renderOption(item) : item.name}</Text>
                )}
              </div>
            );
          })
        ) : (
          <CheckboxGroup className="gap-0" value={checkedValues} onValueChange={handleGroupChange}>
            {listOptions?.map((item) => {
              const isSystemStatus = item.id === SYSTEM_STATUS_ID;
              const checkboxId = `category-filter-checkbox-${item.id}`;

              return (
                <div
                  key={item.id}
                  className={cn(
                    'flex min-h-9 items-center px-2 py-2',
                    !isSystemStatus && !item.disabled && 'hover:bg-gray-100',
                  )}
                >
                  {isSystemStatus ? (
                    <Text variant="body2" color="grey" italic>
                      {item.name}
                    </Text>
                  ) : (
                    <Field orientation="horizontal" data-disabled={item.disabled}>
                      <Checkbox
                        id={checkboxId}
                        value={encodeOptionId(item.id)}
                        disabled={item.disabled}
                        aria-label={item.name}
                      />
                      <FieldLabel htmlFor={checkboxId}>
                        {renderOption ? renderOption(item) : item.name}
                      </FieldLabel>
                    </Field>
                  )}
                </div>
              );
            })}
          </CheckboxGroup>
        )}
      </div>
    </div>
  );
};

export default CategoryFilter;
